import { FC } from 'react';
import { Tooltip as UITooltip } from '@/components/ui';
import { Users } from 'lucide-react';
import { MxmLead } from '../types';

interface LeadPipelineProps {
    leads: MxmLead[];
    leadsByStatus: Array<{ name: string; value: number; color: string }>;
}

export const LeadPipeline: FC<LeadPipelineProps> = ({ leads, leadsByStatus }) => {
    return (
        <div className="bg-white rounded-2xl p-6 lg:col-span-2 overflow-hidden relative border border-slate-200">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-slate-100/80 to-slate-50/40 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-50/50 to-slate-50/30 rounded-full blur-2xl" />

            <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                    <UITooltip content="A breakdown of how many leads are in each stage of your automated sales funnel." position="right">
                        <div>
                            <h3 className="text-base font-bold text-slate-800">Lead Automation Pipeline</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Real-time automation funnel</p>
                        </div>
                    </UITooltip>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        <span className="text-xs font-medium text-slate-600">{leads.length} Active</span>
                    </div>
                </div>

                {leadsByStatus.length > 0 ? (
                    <>
                        {/* Pipeline Flow */}
                        <div className="flex items-center justify-between gap-2 mb-6 overflow-x-auto pb-4 p-2 -m-2 custom-scrollbar-hide">
                            {leadsByStatus.map((item, index) => {
                                const percentage = Math.round((item.value / Math.max(leads.length, 1)) * 100);
                                const isSuccess = item.name === 'booked';
                                const isFinal = item.name === 'inactive' || item.name === 'canceled';

                                return (
                                    <div key={item.name} className="flex items-center flex-1 min-w-0">
                                        {/* Stage Card */}
                                        <div className={`relative flex-1 min-w-[100px] p-4 rounded-xl border transition-all hover:scale-[1.02] cursor-pointer group
                      ${isSuccess ? 'bg-green-50 border-green-200 hover:bg-green-100' :
                                                isFinal ? 'bg-slate-50 border-slate-200 hover:bg-slate-100' :
                                                    'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                                        >
                                            {/* Subtle shadow on hover */}
                                            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                                                style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />

                                            <div className="relative">
                                                {/* Circular Progress */}
                                                <div className="relative w-12 h-12 mx-auto mb-3">
                                                    <svg className="w-12 h-12 transform -rotate-90">
                                                        <circle cx="24" cy="24" r="20" stroke="#e2e8f0" strokeWidth="4" fill="none" />
                                                        <circle
                                                            cx="24" cy="24" r="20"
                                                            stroke={item.color}
                                                            strokeWidth="4"
                                                            fill="none"
                                                            strokeDasharray={`${(percentage / 100) * 125.6} 125.6`}
                                                            strokeLinecap="round"
                                                            className="transition-all duration-700"
                                                        />
                                                    </svg>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <span className="text-sm font-bold text-slate-700">{item.value}</span>
                                                    </div>
                                                </div>

                                                {/* Label */}
                                                <p className="text-[10px] font-medium text-center text-slate-600 capitalize truncate">
                                                    {item.name}
                                                </p>
                                                <p className="text-[9px] text-center text-slate-400 mt-0.5">{percentage}%</p>
                                            </div>
                                        </div>

                                        {/* Connector Arrow */}
                                        {index < leadsByStatus.length - 1 && (
                                            <div className="flex-shrink-0 px-1">
                                                <div className="w-6 h-[2px] bg-gradient-to-r from-slate-300 to-slate-200 relative">
                                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[4px] border-l-slate-300" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Stats Summary */}
                        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-200">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-slate-800">{leads.filter(l => l.status === 'new').length}</p>
                                <p className="text-[10px] text-slate-500">New Today</p>
                            </div>
                            <div className="text-center border-x border-slate-200">
                                <p className="text-2xl font-bold text-green-600">{leads.filter(l => l.status === 'booked').length}</p>
                                <p className="text-[10px] text-slate-500">Converted</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">
                                    {Math.round((leads.filter(l => l.status === 'booked').length / Math.max(leads.length, 1)) * 100)}%
                                </p>
                                <p className="text-[10px] text-slate-500">Success Rate</p>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                            <Users className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-500">No leads in pipeline yet</p>
                        <p className="text-xs text-slate-400 mt-1">Leads will flow through here automatically</p>
                    </div>
                )}
            </div>
        </div>
    );
};
